/**
 * File: Main.tsx
 * Path: apps/admin/src/Main.tsx
 * Description: Application entry point. Bootstraps the React environment and mounts the root component. Updated to reflect moved globals.css location.
 */

import React from "react";
import ReactDOM from "react-dom/client";
import { AppRoot } from "./AppRoot";
import "./globals.css";
import { initAppCheck } from "@idk/config/firebase/appCheckService";
import { registerServiceWorker } from "@/shared/utils/registerServiceWorkerUtils";
import { startErrorLog } from "@/shared/utils/errorLogUtils";
import { applyColorScheme, readColorScheme, watchDeviceColorScheme } from "@/modules/layout/utils/colorSchemeUtils";

// index.html already applied the saved colours; this keeps "Follow my
// device" in step if the device switches while the admin is open.
applyColorScheme(readColorScheme());
watchDeviceColorScheme();

// Crashes and failures, shown in System > Diagnostics (this device only).
startErrorLog();
initAppCheck();
// Installable app, notifications and the offline screen (public/sw.js).
registerServiceWorker();

// Pages load section by section. A tab opened before a deploy asks for a
// section file that the new build replaced; reload once to fetch the new
// build. The flag stops a loop if the file is really missing.
window.addEventListener("vite:preloadError", (event) => {
  try {
    if (sessionStorage.getItem("idk-admin-reloaded-for-update") === "1") return;
    sessionStorage.setItem("idk-admin-reloaded-for-update", "1");
  } catch {
    return;
  }
  event.preventDefault();
  window.location.reload();
});

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Failed to find the root element");
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <AppRoot />
  </React.StrictMode>,
);
